import SubtitlesOff from '@mui/icons-material/SubtitlesOff';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Invoice_Read, InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import InvoicesTabContent from '~components/Invoice/InvoicesTabContent';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import { SmallTabs } from '~components/Tabs/SmallTabs';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useInvoiceLineItems from '~hooks/useInvoiceLineItems/useInvoiceLineItems';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { AllFilesQueryContextProvider } from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/AllFilesQueryContext';
import AllFilesTab from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/AllFilesTab';
import { LoadsQueryContextProvider } from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import LoadsTab from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsTab';
import MessagingProviders from '~pages/Messages/MessagingProviders';
import useMessagingJobUnreadCountQuery from '~queries/useMessagingJobUnreadCountQuery';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobMessagingTab from './components/JobMessaging/JobMessagingTab';
import { JobDetails } from './JobDetails';
import { LoadDetails } from './LoadDetails';

interface JobInvoicesProps {
  currentCategory: InvoiceCategoryFilter;
  hideMap?: boolean;
  job: Job;
  reloadAfterApproved?: boolean;
}

export const JobInvoices = observer(
  ({ currentCategory, hideMap, job, reloadAfterApproved }: JobInvoicesProps) => {
    const theme = useTheme();
    const { approveInvoice, unapproveInvoice, getJobPayable, getJobReceivable } =
      useInvoices();
    const { currencyFormatter } = useCompanyCurrency();
    const { userStore } = useStores();
    const [invoice, setInvoice] = useState<Invoice | null | undefined>(undefined);
    const [subscriptionKey, setSubscriptionKey] = useState<string>('');
    const [shouldRefetchInvoice, setShouldRefetchInvoice] = useState(false);
    const { getAllInvoiceLineItems } = useInvoiceLineItems();
    const { getAllInvoiceAddOnCharges } = useAddOns();

    const companyId = getEffectiveUserCompanyId(userStore);

    const subscribeToInvoiceRTU = useCallback(() => {
      // Create unique key for this component instance
      const key = `job_${job.id}_${currentCategory}_${Date.now()}`;
      setSubscriptionKey(key);

      subscribeWithMultipleHandlers(
        realTimeChannels.InvoiceUpdateChannel,
        companyId,
        key,
        ({ data }: { data: Invoice_Read }) => {
          const updatedInvoice = Invoice.parse(data);

          // Only process updates relevant to this job
          if (updatedInvoice.job?.id !== job.id) {
            return;
          }

          setInvoice((state) => {
            if (state?.id === updatedInvoice.id) {
              return updatedInvoice;
            }

            // necessary for when a new invoice is coming in via RTU
            if (!state && job.id === updatedInvoice.job?.id) {
              setShouldRefetchInvoice(true);
            }

            return state;
          });
          // This ensures that the invoice line items and add-on charges are fetched with the latest invoice data
          // if the payables or receivables tab is open during the RTU update
          getAllInvoiceLineItems(
            companyId,
            String(updatedInvoice.id),
            updatedInvoice.materialRateItemized,
          );
          getAllInvoiceAddOnCharges(companyId, String(updatedInvoice.id));
        },
        {
          job_id: job.id,
          category: currentCategory,
        },
      );
    }, [
      companyId,
      job.id,
      currentCategory,
      getAllInvoiceLineItems,
      getAllInvoiceAddOnCharges,
    ]);

    const getInvoice = async (jobId: string) => {
      let invoice;

      const func =
        currentCategory === InvoiceCategoryFilter.PAYABLES
          ? getJobPayable
          : getJobReceivable;
      try {
        invoice = await func(jobId, companyId);
      } finally {
        setInvoice(invoice ?? null);
      }
    };

    useEffect(() => {
      getInvoice(job.id);
    }, [currentCategory, job.id]);

    useEffect(() => {
      if (shouldRefetchInvoice) {
        setShouldRefetchInvoice(false);
        getInvoice(job.id);
      }
    }, [shouldRefetchInvoice, getInvoice, job.id]);

    useEffect(() => {
      if (!subscriptionKey) {
        // Subscribe to RTU updates
        subscribeToInvoiceRTU();
      }

      // Cleanup function
      return () => {
        if (subscriptionKey) {
          unsubscribeHandler(
            realTimeChannels.InvoiceUpdateChannel,
            companyId,
            subscriptionKey,
          );
        }
      };
    }, [job.id, currentCategory, companyId, subscriptionKey]);

    if (invoice === null) {
      return (
        <Box
          key={job.id}
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          {' '}
          <Box
            key={job.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              width: '100%',
            }}
          >
            <SubtitlesOff />
            <Typography
              color={theme.brandV2.colors.treadBlack}
              variant="body1"
              sx={{ textAlign: 'center', fontWeight: 600 }}
            >
              {t('approvals.driver_day.no_invoices')}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      invoice && (
        <InvoicesTabContent
          invoice={invoice}
          job={job}
          utils={{
            currencyFormatter,
            approveInvoice: async (invoice: Invoice) => {
              const updatedInvoice = await approveInvoice(invoice);
              setInvoice(updatedInvoice);

              if (reloadAfterApproved) {
                await getInvoice(job.id);
              }
            },
            unapproveInvoice: async (invoice: Invoice) => {
              const updatedInvoice = await unapproveInvoice(invoice);
              setInvoice(updatedInvoice);

              if (reloadAfterApproved) {
                await getInvoice(job.id);
              }
            },
          }}
          isDayDetailsView={true}
          key={invoice.id}
          customCategory={currentCategory}
          reload={() => getInvoice(job.id)}
          hideMap={hideMap}
        />
      )
    );
  },
);

interface JobDetailsContainerProps {
  details: Job;
  readOnly: boolean;
}

function JobDetailsContainer({ details, readOnly }: JobDetailsContainerProps) {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { canApproveDriverDays } = userStore.getPermissions();
  const { isViewOnlyUser } = userStore;
  const { getJobById } = useJob();
  const [detailedJob, setDetailedJob] = useState<Job>(details);
  const { unreadCount: jobUnreadCount } = useMessagingJobUnreadCountQuery(detailedJob);
  const [currentCategory, setCurrentCategory] = useState<InvoiceCategoryFilter>(
    InvoiceCategoryFilter.PAYABLES,
  );

  // Set detailedJob if the details passed in changes
  // We shouldn't need to store the state at this level, but see note below
  useEffect(() => {
    setDetailedJob(details);
  }, [details]);

  // Not sure what case we would have to reload the job details given that the parentshould
  // Be responsible for providing the updated details
  // However, will keep this here for now until there is more time to investigate
  useEffect(() => {
    if (!details.id) {
      return;
    }

    getJobById(details.id, companyId).then((job) => {
      if (job) {
        setDetailedJob(job);
      }
    });
  }, [details.id, companyId]);

  const tabs = useMemo(() => {
    const payablesTabId = `payables_${details.id}`;
    const receivablesTabId = `receivables_${details.id}`;

    let allTabs = [
      {
        label: t('order.form.overview'),
        id: `overview_${details.id}`,
        content: (
          <LoadDetails
            details={detailedJob}
            readOnly={readOnly}
            reload={async () => {
              await getJobById(details.id, companyId).then((job) => {
                if (job) {
                  setDetailedJob(job);
                }
              });
            }}
          />
        ),
        isVisible: true,
      },
      {
        label: t('order.form.loads'),
        id: `loads_${details.id}`,
        content: (
          <LoadsQueryContextProvider job={details} companyId={companyId}>
            <LoadsTab />
          </LoadsQueryContextProvider>
        ),
        isVisible: true,
      },
      {
        label: t('dispatch.job.details'),
        id: `details_${details.id}`,
        content: <JobDetails details={detailedJob} />,
        isVisible: true,
      },
      ...(canApproveDriverDays
        ? [
            {
              label: t('dispatch.job.payables'),
              id: payablesTabId,
              content: <JobInvoices currentCategory={currentCategory} job={details} />,
              onSelected: () => {
                setCurrentCategory(InvoiceCategoryFilter.PAYABLES);
              },
              isVisible: !isViewOnlyUser,
            },
            {
              label: t('dispatch.job.receivables'),
              id: receivablesTabId,
              content: <JobInvoices currentCategory={currentCategory} job={details} />,
              onSelected: () => {
                setCurrentCategory(InvoiceCategoryFilter.RECEIVABLES);
              },
              isVisible: !isViewOnlyUser,
            },
            {
              label: t('approvals.files.all_files_tab.title'),
              id: `all_files_${details.id}`,
              content: (
                <AllFilesQueryContextProvider job={details}>
                  <AllFilesTab />
                </AllFilesQueryContextProvider>
              ),
              isVisible: !isViewOnlyUser,
            },
          ]
        : []),
      {
        accentColor: jobUnreadCount > 0 ? theme.brandV2.colors.treadRed : undefined,
        endAdornment: <UnreadCountBadge count={jobUnreadCount} />,
        label: t('messaging.job_tab.title'),
        id: `messaging_${details.id}`,
        content: <JobMessagingTab job={detailedJob} readOnly={readOnly} />,
        // A driverless job is refused on create, so there is no one to message yet.
        isVisible: detailedJob.messagingEnabled && Boolean(detailedJob.driver),
      },
    ];

    allTabs = allTabs.filter((tab) => tab.isVisible);

    return allTabs;
  }, [
    currentCategory,
    detailedJob,
    details.id,
    isViewOnlyUser,
    companyId,
    jobUnreadCount,
    readOnly,
    theme,
  ]);

  return (
    <MessagingProviders>
      <SmallTabs sx={{ p: 2 }} tabs={tabs} />
    </MessagingProviders>
  );
}

export { JobDetailsContainer };
